/**
 * formatBusinessHours — turn the `settings/store.hours` object into a compact,
 * human-readable summary for the storefront Footer (STORY-03.1.03 AC-5).
 *
 * Input shape (written by the admin Store Profile tab, StoreProfileTab.jsx):
 *   {
 *     monday:    { open: '09:00', close: '18:00', closed: false },
 *     ...
 *     sunday:    { open: '09:00', close: '18:00', closed: true },
 *   }
 *
 * Output: an array of { label, value, closed } rows where consecutive days
 * with identical hours are collapsed into a range, e.g.
 *   [ { label: 'Mon–Sat', value: '9am – 6pm', closed: false },
 *     { label: 'Sun',     value: 'Closed',    closed: true } ]
 *
 * Defensive by design (TESTPLAN-03.1.03 risk: "hours object missing a day key
 * crashes footer renderer"): a missing/partial day is treated as Closed, and a
 * null/undefined/non-object input returns [] so the Footer simply omits the
 * hours block instead of throwing.
 */

const DAY_ORDER = [
  ['monday', 'Mon'],
  ['tuesday', 'Tue'],
  ['wednesday', 'Wed'],
  ['thursday', 'Thu'],
  ['friday', 'Fri'],
  ['saturday', 'Sat'],
  ['sunday', 'Sun'],
];

// '09:00' → '9am', '18:00' → '6pm', '13:30' → '1:30pm'. Returns the raw input
// unchanged if it isn't a recognisable 24h HH:MM string.
function formatTime(t) {
  if (typeof t !== 'string' || !/^\d{1,2}:\d{2}$/.test(t)) return t || '';
  const [hStr, mStr] = t.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  // Reject out-of-range times (e.g. '25:99') — return the raw string rather
  // than rendering nonsense like '1:99pm'. Only reachable via a direct
  // Firestore edit; the admin UI uses <input type="time">. (Code-review N2.)
  if (Number.isNaN(h) || Number.isNaN(m) || h > 23 || m > 59) return t;
  const period = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${period}` : `${h12}:${String(m).padStart(2, '0')}${period}`;
}

function dayEntry(hours, id) {
  const h = hours && typeof hours === 'object' ? hours[id] : null;
  if (!h || h.closed === true) return { closed: true, sig: 'closed' };
  const open = h.open || '09:00';
  const close = h.close || '18:00';
  return { closed: false, open, close, sig: `${open}-${close}` };
}

export function formatBusinessHours(hours) {
  // Arrays are objects too — treat any non-day-shaped value as "no hours" and
  // omit the block, rather than collapsing it into a spurious Closed row.
  // (Code-review N1.)
  if (!hours || typeof hours !== 'object' || Array.isArray(hours)) return [];

  const groups = [];
  for (const [id, short] of DAY_ORDER) {
    const e = dayEntry(hours, id);
    const last = groups[groups.length - 1];
    if (last && last.sig === e.sig) {
      last.endLabel = short;
    } else {
      groups.push({ sig: e.sig, startLabel: short, endLabel: short, ...e });
    }
  }

  return groups.map((g) => ({
    label: g.startLabel === g.endLabel ? g.startLabel : `${g.startLabel}–${g.endLabel}`,
    value: g.closed ? 'Closed' : `${formatTime(g.open)} – ${formatTime(g.close)}`,
    closed: g.closed,
  }));
}

export { formatTime };
export default formatBusinessHours;
